import { featureFlags } from '@seen/core/featureFlags'
import { invariant } from '@seen/core/invariant'
import { ExtendedObservation } from '@seen/core/models/observation'
import { observationAnswerSetId } from '@seen/core/observationReferences'
import { referenceStringToId } from '@seen/core/references'
import { observationTemplate } from '@seen/core/terminology/observationTemplates'
import { Button, cn, Separator } from '@seen/ui'
import { IconTrash } from '@tabler/icons-react'
import type { DefaultError } from '@tanstack/react-query'
import { type JSX, useContext } from 'react'
import { toast } from 'sonner'
import { ActivityList } from '#src/components/activity/ActivityList.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { ObservationActivity } from '#src/components/rich-text-editor/ObservationNode/ObservationActivity.tsx'
import {
  ObservationChoiceEditor,
  ObservationReferenceEditor,
  ObservationTextEditor,
} from '#src/components/rich-text-editor/ObservationNode/ObservationBoundEditors.tsx'
import { ObservationComponentsEditor } from '#src/components/rich-text-editor/ObservationNode/ObservationComponentsEditor.tsx'
import { ObservationHistory } from '#src/components/rich-text-editor/ObservationNode/ObservationHistory.tsx'
import { ObservationNodeFieldForm } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFieldForm.tsx'
import { ObservationNodeFrame } from '#src/components/rich-text-editor/ObservationNode/ObservationNodeFrame.tsx'
import { ObservationNoteFieldForm } from '#src/components/rich-text-editor/ObservationNode/ObservationNoteFieldForm.tsx'
import type { ObservationNodeAttributes } from '#src/components/rich-text-editor/ObservationNode/types.ts'
import { useClearObservationAnswer } from '#src/components/rich-text-editor/ObservationNode/useClearObservationAnswer.ts'
import { NodeRecordContext } from '#src/components/rich-text-editor/ObservationNode/useNodeRecord.ts'
import { useObservationParticipant } from '#src/components/rich-text-editor/ObservationNode/useObservationParticipant.ts'
import { useRemoveObservationRecord } from '#src/components/rich-text-editor/ObservationNode/useRemoveObservationRecord.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDeleteConfirmationStore } from '#src/hooks/useDeleteConfirmationStore.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useGroupedObservationHistory } from '#src/hooks/useObservationHistory.ts'
import { useRead } from '#src/hooks/useRead.ts'

export interface ObservationDialogContentProps {
  attributes: ObservationNodeAttributes
  // Off where the node's own line is the value's editor (the vitals), so the
  // dialog cannot write a value that line does not show.
  showValue?: boolean
  // Given when the dialog was opened from a node that can be changed: the
  // delete then also puts that node back to unanswered.
  onCleared?: () => void
  // Opened from the node's note: start in the note editor.
  initialFocus?: 'note'
}

// The details of a recorded observation: its date, deletion, value, earlier
// values and activity.
export function ObservationDialogContent({
  attributes,
  showValue = true,
  onCleared,
  initialFocus,
}: ObservationDialogContentProps): JSX.Element {
  const {
    reference,
    codingDisplay,
    scaleType,
    answerOptions,
    answerValueSet,
    referenceResourceType,
    draft,
  } = attributes
  invariant(reference || draft, 'reference or draft is required')
  invariant(scaleType, 'scaleType is required')

  const removeRecord = useRemoveObservationRecord()
  const openDeleteConfirmation = useDeleteConfirmationStore(
    (state) => state.open
  )
  // A draft is the note's to hold, so it has no record to read or to remove.
  const node = useContext(NodeRecordContext)
  const { data: recorded } = useRead(
    ExtendedObservation,
    draft ? undefined : reference
  )
  const observation = draft ? node?.observation : recorded
  const editorParticipant = useObservationParticipant()
  const clearAnswer = useClearObservationAnswer()
  const inlineCapture = useFeatureFlag(featureFlags.structuredCapture)
  const template = inlineCapture ? observationTemplate(attributes) : undefined
  const coding = { system: attributes.system, code: attributes.code }
  const history = useGroupedObservationHistory({
    participant: inlineCapture
      ? (observation?.subject ??
        (draft ? (editorParticipant ?? undefined) : undefined))
      : undefined,
    coding,
    exceptReference: reference,
    exceptGroupId: observation
      ? observationAnswerSetId(observation.identifiers())
      : null,
    limit: 5,
  }).flatMap((group) => group.members)

  return (
    <DialogContent className="gap-6">
      <div className="flex items-center justify-between gap-2 pt-1 pr-10 pb-4">
        <ObservationNodeFieldForm
          instance={observation}
          valueFn={(o) => o.effectiveDateTime}
          patchFn={(o, value) => {
            invariant(value, 'Effective date is required')
            o.effectiveDateTime = value
          }}
          render={({ field }) => (
            <DatePicker
              {...field}
              placeholder="Effective Date"
              className="h-10"
            />
          )}
        />
        {observation && !observation.isEnteredInError && (
          <Button
            variant="ghost"
            size="icon"
            aria-label="Delete"
            onClick={() => {
              if (draft) {
                // Nothing is in the chart yet, so there is nothing to confirm.
                if (onCleared) {
                  onCleared()
                } else {
                  void node?.remove()
                }
                return
              }
              if (onCleared) {
                clearAnswer({ observation, label: codingDisplay, onCleared })
                return
              }
              openDeleteConfirmation({
                title: `Delete "${codingDisplay ?? 'observation'}"?`,
                description:
                  'This will mark the observation as entered-in-error. It can still be recovered by contacting our team.',
                onConfirm: () => {
                  removeRecord(observation)
                    .then(() =>
                      toast.success(`${codingDisplay ?? 'Observation'} deleted`)
                    )
                    .catch((error: DefaultError) => {
                      toast.error(
                        `Failed to delete ${codingDisplay ?? 'observation'}: ${error}`
                      )
                    })
                },
              })
            }}
          >
            <IconTrash className="size-4" />
          </Button>
        )}
      </div>
      <DialogTitle>{codingDisplay}</DialogTitle>
      {showValue && observation && (
        <div className="flex text-sm">
          {template && (
            <ObservationComponentsEditor
              observation={observation}
              template={template}
            />
          )}
          {scaleType === 'Qn' && !attributes.valueKind && !template && (
            <ObservationDialogNumberInput
              observation={observation}
              attributes={attributes}
              autofocusNote={initialFocus === 'note'}
            />
          )}
          {(scaleType === 'Ord' || scaleType === 'Nom') && (
            <ObservationChoiceEditor
              observation={observation}
              attributes={attributes}
              answerOptions={answerOptions ?? []}
              answerValueSet={answerValueSet ?? null}
              scaleType={scaleType}
              details="none"
              inDetails
              autofocusNote={initialFocus === 'note'}
              className="max-w-(--container-prose)"
            />
          )}
          {scaleType === 'Nar' && (
            <ObservationTextEditor
              observation={observation}
              attributes={attributes}
              details="none"
              className="max-w-(--container-prose)"
            />
          )}
          {scaleType === 'Ref' && referenceResourceType && (
            <ObservationReferenceEditor
              observation={observation}
              attributes={attributes}
              resourceType={referenceResourceType}
              details="none"
              inDetails
              autofocusNote={initialFocus === 'note'}
              className="max-w-(--container-prose)"
            />
          )}
        </div>
      )}
      {(!showValue || template) && inlineCapture && observation && (
        // A vital's value is its own line in the note; here is its clinician
        // note, where a long one is written.
        <div className="flex text-sm">
          <ObservationNodeFrame
            attributes={attributes}
            label="Note"
            details="none"
            noHover
            className="max-w-(--container-prose) min-w-0 grow"
          >
            <ObservationNoteFieldForm
              observation={observation}
              clinician
              autofocus={initialFocus === 'note'}
              className="grow"
            />
          </ObservationNodeFrame>
        </div>
      )}
      {history.length > 0 && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <h5>Previous</h5>
            <ObservationHistory observations={history} coding={coding} />
          </div>
        </>
      )}
      {reference && (
        <>
          <Separator />
          <div className="flex flex-col gap-4">
            <h5>Activity</h5>
            <ActivityList>
              <ObservationActivity
                codingDisplay={codingDisplay}
                observationId={referenceStringToId(reference)}
                scaleType={scaleType}
              />
            </ActivityList>
          </div>
        </>
      )}
    </DialogContent>
  )
}

function ObservationDialogNumberInput({
  observation,
  attributes,
  autofocusNote,
}: {
  observation: ExtendedObservation
  attributes: ObservationNodeAttributes
  autofocusNote: boolean
}): JSX.Element {
  const practitioner = useCurrentPractitioner()
  return (
    <ObservationNodeFrame
      attributes={attributes}
      label={observation.display}
      details="none"
      className="max-w-(--container-prose) min-w-0"
      noHover
    >
      <ObservationNodeFieldForm
        instance={observation}
        valueFn={(o) => o.valueQuantity}
        patchFn={(o, v) => {
          o.updateValueQuantityValue(v ?? undefined)
          o.performerPractitioner = practitioner.reference
        }}
        debounceTyping
        render={({ field: { value, onChange, ...fieldRest } }) => (
          <input
            type="number"
            value={value ?? ''}
            className="w-[8ch] rounded-[calc(var(--radius)-5px)] pl-1 placeholder:text-placeholder focus-visible:ring-2 focus-visible:ring-zinc-950 focus-visible:outline-hidden"
            onChange={(e) =>
              onChange(
                Number.isNaN(e.target.valueAsNumber)
                  ? null
                  : e.target.valueAsNumber
              )
            }
            placeholder="Value"
            {...fieldRest}
          />
        )}
      />
      {observation?.valueUnit && (
        <span className="text-foreground-secondary">
          &nbsp;{observation.valueUnit}
        </span>
      )}
      &nbsp;&nbsp;
      <div
        className="mr-[0.1875rem]" // Accounts for the shift below.
      />
      <ObservationNoteFieldForm
        observation={observation}
        autofocus={autofocusNote}
        className={cn(
          'grow', // So that the editor can grow to fill the available horizontal space when the content goes onto multiple lines.
          '-ml-[0.1875rem]' // For visual alignment when the content goes onto multiple lines.
        )}
      />
    </ObservationNodeFrame>
  )
}
